import { useEffect, useSyncExternalStore } from 'react';

import { fetchCockpitModules } from '../services/cockpitModulesService';

const NONE: ReadonlySet<string> = new Set();

/**
 * The modules switched off, shared by every menu of the page (PLAN-GENERICITE § 3.7). A small store
 * with listeners: a switch in Settings → Modules re-reads it and every mounted menu follows at once
 * (audit of 07/10). A failed read shows every module (the menu never loses the base) and is tried
 * again by the next screen that mounts.
 */
let state: ReadonlySet<string> = NONE;
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: ReadonlySet<string>): void {
  state = next;
  for (const listener of listeners) listener();
}

function read(): Promise<void> {
  inflight ??= fetchCockpitModules()
    .then((answer) => {
      loaded = answer.ok;
      publish(answer.ok ? new Set(answer.modules.filter((m) => !m.enabled).map((m) => m.slug)) : NONE);
    })
    .catch(() => {
      loaded = false;
      publish(NONE);
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Reads the modules again (after a switch in Settings): every mounted menu follows. */
export function forgetSwitchedOffModules(): void {
  loaded = false;
  void read();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The cockpit modules switched off, for the navigation; none while reading or when unreadable. */
export function useSwitchedOffModules(): ReadonlySet<string> {
  const off = useSyncExternalStore(subscribe, () => state, () => NONE);
  useEffect(() => {
    if (!loaded) void read();
  }, []);
  return off;
}
